import React, { useState, useEffect } from 'react';
import useApiGet from '../hooks/getData';
import useApiPost from '../hooks/postData';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import Searchbar from '../../components/Search';

function GreedyCafeConfig() {
  const { getData } = useApiGet();
  const { postData } = useApiPost();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const [roundDuration, setRoundDuration] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingItem, setSavingItem] = useState(null);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getData('/game/greedy-cafe/config');
      setRoundDuration(res?.data?.round_duration_seconds ?? "");
      setItems(res?.data?.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSaveSettings = async () => {
    if (!roundDuration || roundDuration < 1) {
      toast.error("Please enter a valid round duration");
      return;
    }
    setSavingSettings(true);
    try {
      const res = await postData('/game/greedy-cafe/update-settings', {
        round_duration_seconds: Number(roundDuration),
      });
      if (res?.success) {
        toast.success(res.message || "Settings updated!");
      } else {
        toast.error(res?.message || "Failed to update");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleItemChange = (itemKey, field, value) => {
    setItems((prev) =>
      prev.map((it) => (it.item_key === itemKey ? { ...it, [field]: value } : it))
    );
  };

  const handleSaveItem = async (item) => {
    setSavingItem(item.item_key);
    try {
      const res = await postData('/game/greedy-cafe/update-item', {
        item_key: item.item_key,
        multiplier: Number(item.multiplier),
        weight_percent: Number(item.weight_percent),
        sort_order: Number(item.sort_order),
      });
      if (res?.success) {
        toast.success(res.message || "Item updated!");
      } else {
        toast.error(res?.message || "Failed to update item");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setSavingItem(null);
    }
  };

  const totalWeight = items.reduce((sum, it) => sum + (Number(it.weight_percent) || 0), 0);

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <Searchbar />
      <div className="px-4 pb-10 xl:px-6">

        <h2 className="text-[#000000] font-semibold font-poppins text-xl pt-3 pb-5 dark:text-darkText">
          Greedy Cafe — Game Config
        </h2>

        {loading ? (
          <p className="font-poppins dark:text-darkText">Loading...</p>
        ) : (
          <>
            {/* Round Duration */}
            <div className="border border-opacity-gradient dark:border-[#1F1F1F] bg-[#FFFFFF] dark:bg-primary rounded-lg p-6 mb-6">
              <h3 className="text-base font-semibold font-poppins text-header pb-4">Round Settings</h3>
              <div className="flex items-end gap-4">
                <div>
                  <label className="text-sm font-semibold font-poppins dark:text-darkText">
                    Round Duration (seconds)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={roundDuration}
                    onChange={(e) => setRoundDuration(e.target.value)}
                    className="border border-opacity-gradient dark:bg-transparent dark:text-darkText dark:border-borderColor rounded-lg w-40 py-3 my-1 px-4 focus:outline-none focus:ring-1 focus:ring-header"
                  />
                </div>
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  style={{ backgroundColor: '#593A99' }}
                  className="px-8 py-3 text-sm font-medium text-white rounded-lg font-poppins"
                >
                  {savingSettings ? "Saving..." : "Save"}
                </button>
              </div>
            </div>

            {/* Items Table */}
            <div className="border border-opacity-gradient dark:border-[#1F1F1F] bg-[#FFFFFF] dark:bg-primary rounded-lg p-6">
              <div className="flex items-center justify-between pb-4">
                <h3 className="text-base font-semibold font-poppins text-header">Items</h3>
                <span className={`px-3 py-1 text-sm font-semibold rounded-lg font-poppins ${
                  totalWeight === 100
                    ? "text-[#0D9947] bg-[#0D9947] bg-opacity-15"
                    : "text-[#D92D20] bg-[#D92D20] bg-opacity-15"
                }`}>
                  Total Weight: {totalWeight}%
                </span>
              </div>

              <div className="overflow-hidden border border-gray-200 dark:border-[#1F1F1F] rounded-lg">
                <div className="flex px-4 py-3 bg-[#00000005] dark:bg-[#1F1F1F] text-sm font-semibold font-poppins text-[#5B5B5B] dark:text-tableDarkLarge">
                  <div className="w-[20%]">Item</div>
                  <div className="w-[20%] text-center">Multiplier</div>
                  <div className="w-[20%] text-center">Weight %</div>
                  <div className="w-[20%] text-center">Sort Order</div>
                  <div className="w-[20%] text-center">Action</div>
                </div>
                {items.map((item) => (
                  <div key={item.item_key} className="flex items-center px-4 py-3 border-t border-gray-100 dark:border-[#1F1F1F]">
                    <div className="w-[20%] flex items-center gap-2 font-poppins text-sm dark:text-darkText">
                      <span className="text-lg">{item.emoji}</span>
                      <span>{item.name}</span>
                    </div>
                    <div className="w-[20%] text-center">
                      <input
                        type="number"
                        min="1"
                        value={item.multiplier}
                        onChange={(e) => handleItemChange(item.item_key, 'multiplier', e.target.value)}
                        className="w-20 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-[#1F1F1F] dark:bg-transparent dark:text-darkText rounded-lg focus:outline-none focus:ring-1 focus:ring-header"
                      />
                    </div>
                    <div className="w-[20%] text-center">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={item.weight_percent}
                        onChange={(e) => handleItemChange(item.item_key, 'weight_percent', e.target.value)}
                        className="w-20 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-[#1F1F1F] dark:bg-transparent dark:text-darkText rounded-lg focus:outline-none focus:ring-1 focus:ring-header"
                      />
                    </div>
                    <div className="w-[20%] text-center">
                      <input
                        type="number"
                        min="1"
                        value={item.sort_order}
                        onChange={(e) => handleItemChange(item.item_key, 'sort_order', e.target.value)}
                        className="w-20 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-[#1F1F1F] dark:bg-transparent dark:text-darkText rounded-lg focus:outline-none focus:ring-1 focus:ring-header"
                      />
                    </div>
                    <div className="w-[20%] text-center">
                      <button
                        onClick={() => handleSaveItem(item)}
                        disabled={savingItem === item.item_key}
                        style={{ backgroundColor: '#0D9947' }}
                        className="px-4 py-1.5 text-xs font-medium text-white rounded-lg font-poppins"
                      >
                        {savingItem === item.item_key ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {totalWeight !== 100 && (
                <p className="pt-3 text-xs font-poppins text-[#D92D20]">
                  ⚠️ Weights should add up to 100% for correct game probability.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default GreedyCafeConfig;